'use client';

import { useEffect, useRef } from 'react';
import { useGlobalContext } from '@/context/GlobalContext';
import { getUserDetailsCached, getOrgDetailsCached } from '@/utils/utils';

// Loads the PM (re:billion) widget on every screen, passing the logged-in user's
// name + email + orgId so the widget can identify them. The third-party SDK reads
// its data-* attributes once at load, so we resolve identity BEFORE injecting:
//   • name/email — fast path from GlobalContext (set by the navbar after login),
//     else /api/user/details (cookie-authed, same source as the navbar),
//   • orgId — from /api/org/details (not present on the user object),
//   • public/logged-out screens — still load the widget, just without identity.
// Injected exactly once per document (guarded by id + ref) so the two router trees
// (Pages Router _app + App Router layout) can both mount it without duplicating.

const WIDGET_SRC = 'https://launchpad.rebillion.ai/widget/sdk.js';
const WIDGET_ID = 'pm-rebillion-widget-sdk';

// The widget renders into a CLOSED shadow DOM on this host div (created by sdk.js),
// which lives in OUR light DOM fixed to the bottom-right corner.
const WIDGET_HOST_ID = 'rb-support-widget';
const POS_STORAGE_KEY = 'rbWidgetPosition';

// Make the widget's launcher (FAB) draggable. We can't reach inside the closed
// shadow DOM, but the host div is ours to move. Behavior:
//   • A drag only starts from inside the host's box (the FAB). With a closed shadow
//     we can't tell whether the FAB or the open panel was clicked, so gating on the
//     host box keeps clicks inside the open panel working normally.
//   • Tiny movements stay a click (the widget opens); larger ones become a drag.
//   • The click that trails a real drag is swallowed so the widget doesn't toggle.
//   • The chosen position is persisted in localStorage and kept on-screen on resize.
// Returns a cleanup function.
function enableWidgetDrag(host) {
    if (!host || host.dataset.rbDraggable === '1') return undefined;
    host.dataset.rbDraggable = '1';
    host.style.touchAction = 'none'; // don't scroll the page while dragging on touch

    const FAB_FALLBACK = 56;
    const THRESHOLD = 4; // px before a press counts as a drag rather than a click
    const MARGIN = 8; // keep at least this far from the viewport edges

    const clampAndApply = (left, top) => {
        const r = host.getBoundingClientRect();
        const w = r.width || FAB_FALLBACK;
        const h = r.height || FAB_FALLBACK;
        const x = Math.max(MARGIN, Math.min(left, window.innerWidth - w - MARGIN));
        const y = Math.max(MARGIN, Math.min(top, window.innerHeight - h - MARGIN));
        host.style.left = x + 'px';
        host.style.top = y + 'px';
        host.style.right = 'auto';
        host.style.bottom = 'auto';
    };

    // Restore a previously saved position.
    try {
        const saved = JSON.parse(window.localStorage.getItem(POS_STORAGE_KEY) || 'null');
        if (saved && typeof saved.left === 'number' && typeof saved.top === 'number') {
            clampAndApply(saved.left, saved.top);
        }
    } catch (e) { /* ignore */ }

    let dragging = false;
    let moved = false;
    let startX = 0;
    let startY = 0;
    let originLeft = 0;
    let originTop = 0;
    let pointerId = null;

    const onDown = (e) => {
        if (e.button != null && e.button !== 0) return; // primary button / touch only
        const r = host.getBoundingClientRect();
        if (e.clientX < r.left || e.clientX > r.right || e.clientY < r.top || e.clientY > r.bottom) {
            return; // pointer is on the open panel (outside the FAB box) — don't drag
        }
        delete host.dataset.rbJustDragged;
        dragging = true;
        moved = false;
        startX = e.clientX;
        startY = e.clientY;
        originLeft = r.left;
        originTop = r.top;
        pointerId = e.pointerId;
    };

    const onMove = (e) => {
        if (!dragging) return;
        const dx = e.clientX - startX;
        const dy = e.clientY - startY;
        if (!moved) {
            if (Math.hypot(dx, dy) < THRESHOLD) return; // still within click tolerance
            moved = true;
            try { host.setPointerCapture(pointerId); } catch (e2) { /* ignore */ }
        }
        e.preventDefault();
        clampAndApply(originLeft + dx, originTop + dy);
    };

    const onUp = () => {
        if (!dragging) return;
        dragging = false;
        if (pointerId != null) { try { host.releasePointerCapture(pointerId); } catch (e2) { /* ignore */ } }
        if (moved) {
            const r = host.getBoundingClientRect();
            try { window.localStorage.setItem(POS_STORAGE_KEY, JSON.stringify({ left: r.left, top: r.top })); } catch (e2) { /* ignore */ }
            host.dataset.rbJustDragged = '1'; // swallow the trailing click (see onClickCapture)
        }
    };

    // Capture phase so we run before the FAB's own click handler inside the shadow DOM.
    const onClickCapture = (e) => {
        if (host.dataset.rbJustDragged === '1') {
            e.stopPropagation();
            e.preventDefault();
            delete host.dataset.rbJustDragged;
        }
    };

    const onResize = () => {
        const r = host.getBoundingClientRect();
        clampAndApply(r.left, r.top);
    };

    host.addEventListener('pointerdown', onDown);
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
    host.addEventListener('click', onClickCapture, true);
    window.addEventListener('resize', onResize);

    return () => {
        host.removeEventListener('pointerdown', onDown);
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onUp);
        window.removeEventListener('pointercancel', onUp);
        host.removeEventListener('click', onClickCapture, true);
        window.removeEventListener('resize', onResize);
        delete host.dataset.rbDraggable;
    };
}

export default function WidgetLoader() {
    const { user } = useGlobalContext();
    const injectedRef = useRef(false);

    useEffect(() => {
        if (injectedRef.current || typeof document === 'undefined') return undefined;
        if (document.getElementById(WIDGET_ID)) {
            injectedRef.current = true;
            return undefined;
        }

        let cancelled = false;

        const inject = (name, email, orgId) => {
            if (cancelled || injectedRef.current) return;
            if (document.getElementById(WIDGET_ID)) {
                injectedRef.current = true;
                return;
            }
            const s = document.createElement('script');
            s.id = WIDGET_ID;
            s.src = WIDGET_SRC;
            s.async = true;
            s.setAttribute('data-user-name', name || '');
            s.setAttribute('data-user-email', email || '');
            s.setAttribute('data-org-id', orgId || '');
            document.body.appendChild(s);
            injectedRef.current = true;
        };

        (async () => {
            // Identity (name/email): prefer GlobalContext (populated by
            // UserBootstrap and/or Navbar), else fall back to the shared
            // cached /api/user/details helper. Sharing the helper with
            // UserBootstrap means only one network call happens even when
            // both mount at the same instant (module-level in-flight dedup
            // in getUserDetailsCached).
            let name = '';
            let email = '';
            if (user && (user.fullName || user.email)) {
                name = user.fullName || user.userName || '';
                email = user.email || '';
            } else {
                try {
                    const d = await getUserDetailsCached('cookie-auth');
                    if (cancelled) return;
                    if (d) {
                        name = d.fullName || d.userName || '';
                        email = d.email || '';
                    }
                } catch (e) { /* ignore */ }
            }

            // orgId isn't part of the user object — resolve via the same
            // cached helper the rest of the app uses (MenuButton3,
            // TransactionsPage, EnabledStatesSettings). One call per
            // session shared across all callers.
            let orgId = '';
            try {
                const d = await getOrgDetailsCached('cookie-auth');
                if (cancelled) return;
                if (d) {
                    orgId = d.orgId || '';
                }
            } catch (e) { /* ignore */ }

            // Always load the widget — public/logged-out screens just get empties.
            inject(name, email, orgId);
        })();

        return () => { cancelled = true; };
    }, [user]);

    // Make the launcher draggable once the widget SDK has created its host element.
    useEffect(() => {
        if (typeof document === 'undefined') return undefined;
        let cleanup = null;
        let stopped = false;
        const attach = (host) => { if (!stopped) cleanup = enableWidgetDrag(host); };

        const existing = document.getElementById(WIDGET_HOST_ID);
        if (existing) {
            attach(existing);
            return () => { stopped = true; if (cleanup) cleanup(); };
        }

        // The host is appended to <body> after sdk.js loads — watch for it.
        const obs = new MutationObserver(() => {
            const h = document.getElementById(WIDGET_HOST_ID);
            if (h) {
                obs.disconnect();
                attach(h);
            }
        });
        obs.observe(document.body, { childList: true });
        return () => {
            stopped = true;
            obs.disconnect();
            if (cleanup) cleanup();
        };
    }, []);

    return null;
}